import React, { FunctionComponent } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { useTheme } from 'styled-components/native'

import {
  CLIENT_SELF_PAY_STATUS,
  SELF_PAY_ZENDESK_ARTICLE_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { openUrl } from '../../utils/expo-linking'
import { Link } from '../link/Link'

const messages = defineMessages({
  noSessionsRemaining: {
    defaultMessage:
      '{clientFirstName} has <warning>run out of covered sessions</warning>. {customerName} offers a self-pay option for clients to pay for sessions themselves. If you and {clientFirstName} agree to continue, <link>you can learn more about self-pay here.</link>',
    description: 'Banner text informing that the client is almost out of covered sessions and they can use self-pay',
  },
  oneSessionRemaining: {
    defaultMessage:
      '{clientFirstName} <warning>is almost out of covered sessions</warning>. {customerName} offers a self-pay option for clients to pay for sessions themselves. If you and {clientFirstName} agree to continue, <link>you can learn more about self-pay here.</link>',
    description: 'Banner text informing that the client is almost out of covered sessions and they can use self-pay',
  },
})

export type SelfPayContinuationOfCareBannerProps = {
  clientDetails: ClientObject
  sessionsRemaining: number
}

export const SelfPayContinuationOfCareBanner: FunctionComponent<SelfPayContinuationOfCareBannerProps> = ({
  clientDetails,
  sessionsRemaining,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const isClientSelfPayEligible = clientDetails?.self_pay_status === CLIENT_SELF_PAY_STATUS.ELIGIBLE

  const clientFirstName = clientDetails?.preferred_first_name || clientDetails?.first_name || ''
  const customerName = clientDetails?.displayed_employer ?? '-'
  const message = sessionsRemaining === 0 ? messages.noSessionsRemaining : messages.oneSessionRemaining

  return isClientSelfPayEligible && sessionsRemaining <= 1 ? (
    <InlineWarningBanner
      testId={'SelfPayContinuationOfCareBanner'}
      text={formatMessage(message, {
        clientFirstName,
        customerName,
        link: (text: string) => (
          <Link
            size={BodyTextSize.SMALL}
            style={{ textDecorationLine: 'underline', color: colors.textWarning }}
            onPress={() => openUrl(SELF_PAY_ZENDESK_ARTICLE_URL)}
            text={text}
          />
        ),
        warning: (text: string) => (
          <BodyText size={BodyTextSize.SMALL} color={colors.textWarning} style={{ fontWeight: '500' }} text={text} />
        ),
      })}
      textSize={BodyTextSize.SMALL}
      textColor={colors.textWarning}
      iconColor={colors.textWarning}
      marginBottom='0px'
    />
  ) : null
}
